import React, { useEffect, useState } from "react";
import { api, type Me } from "./api";
import { appNow } from "./clock";
import { eastern } from "./RecordScreen";
import { FLAG_LABEL, HOLD_LABEL, REASON_LABEL, REVIEW_LABEL } from "./ReviewQueue";
import { GatheringView, PassSheetView } from "./PrintCenter";
import { NotificationQueue } from "./NotificationQueue";
import { Concerns } from "./Concerns";

/**
 * Reports 1–4 (build plan §7.2), the Director digest (§7.1 #8) and the
 * System Admin's "Run now" for the scheduled emails (§3). Every screen is for
 * every role; the print view and the CSV are PHI-dense exports (System Admin,
 * Supervisor, RN), so their buttons appear only for those roles and the
 * server checks again. Each view is audited on the server.
 */

const TITLES: Record<string, string> = {
  "1": "Review status",
  "2": "Document requests and attendance holds",
  "3": "Expected at the med station",
  "4": "18 at check-in and turning 18",
  "5": "Medication seed: changes since the freeze",
  "7": "Med-station status board",
  "8": "Medications received with exceptions",
  "9": "Controlled medication receipt counts",
  "10": "Gathering list",
  "11": "Med-pass sheet",
  "12": "Due now, missed, unverified",
  "13": "Director's report",
  "14": "Counselor watch list",
  "15": "Daily log",
  "16": "Open follow-ups",
  "17": "Parent notifications",
  "18": "Pending provider authorizations",
  "19": "Staff visits log",
  "20": "Supplies below par",
  "22": "Accident list (insurance)",
  "23": "Medication return reconciliation",
  "24": "Controlled-substance audit trail",
  "25": "Season summary",
  "28": "Cafeteria allergy list",
  "29": "Allergies by city",
};
/** Reports that take a window, a city and a day (M3 Stage 3). */
const PASS_REPORTS = ["10", "11", "12"];
/** Reports that take a day only (#15). */
const DAY_REPORTS = ["15"];
const BLURB: Record<string, string> = {
  "1": "To review, reviewed, re-review, family updated after review, and flags by type.",
  "2": "Open document requests with days outstanding and reminders; attendance holds.",
  "3": "Who the registration desk sends to the med station, and why. Printable.",
  "4": "Who is 18 at check-in, and who turns 18 on each day of the session.",
  "5": "Freeze the medication seed at amendments close, then see what families changed since.",
  "7": "Per city: outstanding, in progress, confirmed, pending authorization, held, not brought. A wall-screen view refreshes every minute.",
  "8": "Not brought, received with an exception, or a count that changed: who, when, and whether it is resolved.",
  "9": "Every controlled medication received: count, received by, verified by, when; totals per medication.",
  "10": "Per window and city: who to send to the med station. Names and a count only.",
  "11": "Per window and city: the pass's rows with blank columns, the paper fallback.",
  "12": "Live, per city for the window: due, recorded, due now; today's missed doses and entries waiting for a countersign, with who.",
  "13": "Morning and dinner, the last 24 hours: visits (injuries, illnesses), referrals out, transports, follow-ups open, restrictions, missed doses, dismissals, trends. One line per visit; never clinical notes. Emailed to the Director at the times in Config.",
  "14": "For counselors, per city: name, the restriction or hold and its end date, \"Check on today\", and the RN's one-line note. Nothing else.",
  "18": "Every medication waiting for a provider's authorization: how long, the path, who is chasing it.",
  "19": "Visits and OTC items for staff and volunteers. No parent columns.",
  "28": "For the kitchen: name, city, food allergens, anaphylaxis yes/no. Nothing else. (Was #13 before Stage 2.)",
  "29": "For counselors, per city: name, allergens, severity, carries an auto-injector, action plan on file. (Was #14 before Stage 2.)",
  "15": "Everything on a day, by time: every visit, every dose (Rx and OTC) and every progress note. Printable as the paper fallback.",
  "16": "What is still open: visit follow-ups, entries waiting for a countersign, Call-me-first calls that didn't reach a parent, insurance notices due.",
  "17": "The working queue: email drafts to release (an RN or Supervisor), phone calls not yet reached, what was sent. Overdue drafts are amber.",
  "20": "Supplies below par during the week, with the quantity to order (par minus on hand).",
  "22": "Every injury (not illnesses: the policy is accident-only), one line per incident with the claim form's Part I fields and the notice clock: amber within 14 days of due, red when overdue.",
  "23": "Per student and medication: what should have come back, what did, any discrepancy; a controlled medication's whole chain; what is still unreturned.",
  "24": "Per student: each controlled medication's receipt, every dose (counts before and after, second person), corrections and the return with the parent's signature.",
  "25": "The session in counts, never names: visits by kind and disposition, OTC by item, scheduled doses given, held, refused and missed, referrals, transports, dismissals, notifications sent, staff visits; per city.",
};
const plural = (n: number, word: string) => (n === 1 ? word : `${word}s`);
const HOLD_STATE: Record<string, string> = { Requested: "Decision requested", ...HOLD_LABEL };

const day = (iso: string) => {
  if (!iso) return "";
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? "" : new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric" }).format(t);
};
const longDate = (d: string) => new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "long", month: "long", day: "numeric" }).format(new Date(`${d}T00:00:00Z`));

interface P {
  personKey: string;
  lastName: string;
  firstName: string;
}
const Name = ({ p, link = true }: { p: P; link?: boolean }) =>
  link ? (
    <a className="font-medium text-program underline print:text-black print:no-underline" href={`/review/${encodeURIComponent(p.personKey)}`}>
      {p.lastName}, {p.firstName}
    </a>
  ) : (
    <span className="font-medium">{p.lastName}, {p.firstName}</span>
  );

function Table({ head, rows }: { head: string[]; rows: React.ReactNode[][] }) {
  if (!rows.length) return <p className="text-sm text-slate-600">None.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 text-slate-500">
          <tr>
            {head.map((h) => (
              <th key={h} className="py-1.5 pr-3 font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i} className="align-top break-inside-avoid">
              {r.map((c, j) => (
                <td key={j} className="py-1.5 pr-3">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---- the four reports' bodies (screen and print share them) ----------------------------------------

/* eslint-disable @typescript-eslint/no-explicit-any */
const BOARD_COLS: [string, string][] = [
  ["outstanding", "Outstanding"],
  ["inProgress", "In progress"],
  ["confirmed", "Confirmed"],
  ["pendingAuthorization", "Pending authorization"],
  ["heldNotAuthorized", "Held: not authorized"],
  ["notBrought", "Not brought"],
];

/** Report #7's grid; big type on the wall screen. */
export function Board({ r, big = false, now }: { r: any; big?: boolean; now: number }) {
  const cell = big ? "px-3 py-3 text-3xl font-semibold" : "py-1.5 pr-3";
  const head = big ? "px-3 py-2 text-lg" : "py-1.5 pr-3 font-medium";
  const since = (iso: string) => {
    if (!iso) return "";
    const m = Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
    return `oldest ${Math.floor(m / 60)} h ${m % 60} m`;
  };
  return (
    <>
      {r.askConsentToday.length > 0 && (
        <div className={`mb-3 rounded-md border border-amber-400 bg-amber-50 p-3 text-amber-950 ${big ? "text-2xl" : "text-sm"}`}>
          <p className="font-semibold">Ask consent today (turning 18):</p>
          <p>{r.askConsentToday.map((p: any) => `${p.lastName}, ${p.firstName}${p.city ? ` (${p.city})` : ""}`).join(" · ")}</p>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b border-slate-300 text-slate-600">
            <tr>
              <th className={head}>City</th>
              {BOARD_COLS.map(([, l]) => <th key={l} className={head}>{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {[...r.cities, r.total].map((c: any) => (
              <tr key={c.city} className={`border-b border-slate-100 ${c.city === "Total" ? "bg-slate-50 font-semibold" : ""}`}>
                <td className={big ? "px-3 py-3 text-2xl" : "py-1.5 pr-3"}>{c.city}</td>
                {BOARD_COLS.map(([k]) => (
                  <td key={k} className={`${cell} ${k === "pendingAuthorization" && c[k] ? "text-amber-800" : k === "heldNotAuthorized" && c[k] ? "text-red-800" : ""}`}>
                    {c[k]}
                    {k === "pendingAuthorization" && c.oldestPendingUtc && <span className={`block font-normal ${big ? "text-base" : "text-xs"}`}>{since(c.oldestPendingUtc)}</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const KIND_LABEL: Record<string, string> = { dueNow: "Due now", missed: "Missed", unverified: "Unverified" };

function ReportBody({ id, r, link }: { id: string; r: any; link: boolean }) {
  if (id === "7") return <Board r={r} now={Date.parse(r.generatedAtUtc)} />;
  if (id === "10" || id === "11") {
    if (!r.window) return <p className="text-sm text-slate-600">No medication windows are set (Config: Medication windows).</p>;
    const parts = id === "10" ? r.lists : r.sheets;
    if (!parts.length) return <p className="text-sm text-slate-600">No one has a dose due.</p>;
    return <>{id === "10" ? r.lists.map((g: any) => <GatheringView key={g.city} g={g} />) : r.sheets.map((s: any) => <PassSheetView key={s.city} s={s} />)}</>;
  }
  if (id === "12") {
    const cols: [string, string][] = [["due", "Due in window"], ["recorded", "Recorded"], ["dueNow", "Due now"], ["missed", "Missed today"], ["unverified", "Unverified today"]];
    return (
      <>
        <p className="mb-2 text-sm text-slate-600">{r.window ? `${r.window.name} (${r.window.startTime}–${r.window.endTime})` : "No window"} · {r.date} · {r.city ? (r.city === "(not assigned)" ? "Not assigned" : r.city) : "Every city"} · as of {eastern(r.generatedAtUtc)}</p>
        <Table head={["City", ...cols.map(([, l]) => l)]} rows={[...r.cities, r.total].map((c: any) => [c.city, ...cols.map(([k]) => (c[k] ? <span key={k} className={k === "missed" || k === "dueNow" ? "font-semibold text-amber-900" : ""}>{c[k]}</span> : 0))])} />
        <h3 className="mb-2 mt-5 text-base font-semibold">Who</h3>
        <Table head={["", "Name", "City", "Medication", "Window", "To countersign"]} rows={r.items.map((i: any) => [KIND_LABEL[i.kind] ?? i.kind, <Name key="n" p={i} link={link} />, i.city || "Not assigned", i.medication, i.windowCode, i.who || "—"])} />
      </>
    );
  }
  if (id === "8") {
    return (
      <Table
        head={["Name", "Reference", "City", "Medication", "Exception", "Note", "By", "When", "Resolved", "Status now"]}
        rows={r.lines.map((l: any) => [<Name key="n" p={l} link={link} />, l.personKey, l.city || "—", l.medication, l.exception, l.note || "—", l.byEmail, eastern(l.atUtc), l.resolved ? `Yes: ${l.resolution}` : `No: ${l.resolution}`, l.status])}
      />
    );
  }
  if (id === "28") {
    return (
      <Table
        head={["Name", "City", "Food allergens", "Anaphylaxis"]}
        rows={r.lines.map((l: any) => [<Name key="n" p={l} link={link} />, l.city || "Not assigned", l.foodAllergens, l.anaphylaxis === "Y" ? <span key="a" className="font-bold text-red-800">YES</span> : "No"])}
      />
    );
  }
  if (id === "29") {
    if (!r.cities.length) return <p className="text-sm text-slate-600">None.</p>;
    return (
      <>
        {r.cities.map((c: any) => (
          <div key={c.city} className="mb-5 break-inside-avoid">
            <h3 className="mb-1 text-base font-semibold">{c.city}</h3>
            <Table
              head={["Name", "Allergens", "Severity", "Carries auto-injector", "Plan on file"]}
              rows={c.people.map((l: any) => [<Name key="n" p={l} link={link} />, l.allergens, l.severity || "—", l.autoInjector === "Y" ? "Yes" : "No", l.planOnFile === "Y" ? "Yes" : "No"])}
            />
          </div>
        ))}
      </>
    );
  }
  if (id === "22") {
    if (!r.lines.length) return <p className="text-sm text-slate-600">No injuries recorded.</p>;
    const yn = (v: string) => (v === "Y" ? "Yes" : v === "N" ? "No" : "—");
    return (
      <ul className="divide-y divide-slate-100 text-sm">
        {r.lines.map((l: any) => (
          <li key={l.visitKey} className="break-inside-avoid py-2">
            <p>
              <span className="font-medium">{link ? <a className="text-program underline" href={`/visits/${encodeURIComponent(l.visitKey)}`}>{l.lastName}, {l.firstName}</a> : `${l.lastName}, ${l.firstName}`}</span>
              {" "}· {l.personType === "Staff" ? "Staff" : l.city === "(not assigned)" ? "Not assigned" : l.city} · {eastern(l.occurredAtUtc)} · {l.location || "place not recorded"}
              {l.noticeAlert && <span className={`ml-2 rounded px-1.5 text-xs font-bold ${l.noticeAlert === "overdue" ? "bg-red-600 text-white" : "bg-amber-200 text-amber-950"}`}>{l.noticeAlert === "overdue" ? `Notice overdue (due ${l.noticeDueOn})` : `Notice due ${l.noticeDueOn}`}</span>}
            </p>
            <p>{l.natureOfInjury || "—"}{l.bodyParts ? ` (${l.bodyParts})` : ""}: {l.narrative || "how it happened not recorded"}</p>
            <p className="text-slate-600">Supervised {yn(l.claim.SupervisedActivity)} · sponsored {yn(l.claim.SponsoredActivity)} · scheduled hours {yn(l.claim.ScheduledHours)} · traveling {yn(l.claim.Traveling)} · off premises {yn(l.claim.OffPremises)} · supervising adult: {l.supervisingAdult || "—"}{l.supervisingTitle ? `, ${l.supervisingTitle}` : ""} (saw it: {yn(l.supervisorWasWitness)}) · witnesses: {l.witnesses || "—"}</p>
            <p className="text-slate-600">Outside care: {l.outsideCare === "Y" ? l.outsideCareProvider || "yes" : "no"} · notice: {l.noticeStatus === "Sent" ? `sent ${eastern(l.noticeSentUtc)}` : l.noticeStatus === "Due" ? `due ${l.noticeDueOn}` : "not needed"}{l.packetSentUtc ? ` · packet sent ${eastern(l.packetSentUtc)}` : ""}</p>
          </li>
        ))}
      </ul>
    );
  }
  if (id === "23") {
    const c = r.counts;
    return (
      <>
        <p className="mb-3 text-sm"><b>{c.held}</b> held · <b>{c.returned}</b> returned · <b>{c.notReturned}</b> not returned · <b>{c.disposed}</b> disposed · <b>{c.outstanding}</b> still to return · <b className={c.discrepancies ? "text-red-700" : ""}>{c.discrepancies}</b> {c.discrepancies === 1 ? "discrepancy" : "discrepancies"}</p>
        <Table
          head={["Name", "City", "Medication", "Expected", "Returned", "Status", "To", "Discrepancy", "Note"]}
          rows={r.lines.map((l: any) => [
            <Name key="n" p={l} link={link} />,
            l.city === "(not assigned)" ? "Not assigned" : l.city,
            <span key="m">{l.medication}{l.controlled && <span className="ml-1 rounded bg-violet-100 px-1 text-xs font-semibold text-violet-900">controlled</span>}{l.heldNotAuthorized && <span className="ml-1 text-xs text-slate-500">(held: not authorized)</span>}{l.chain.length > 0 && <ul className="mt-1 list-disc pl-4 text-xs text-slate-600">{l.chain.map((x: string, i: number) => <li key={i}>{x}</li>)}</ul>}</span>,
            l.expected,
            l.returnedQty || "—",
            l.status,
            l.toWhom || "—",
            l.discrepancy ? <b key="d" className="text-red-700">{l.discrepancy}</b> : "—",
            l.note || "—",
          ])}
        />
        <h3 className="mb-1 mt-4 text-base font-semibold">Still to return ({r.unreturned.length})</h3>
        <Table head={["Name", "City", "Medication", "Expected"]} rows={r.unreturned.map((l: any) => [<Name key="n" p={l} link={link} />, l.city === "(not assigned)" ? "Not assigned" : l.city, l.medication, l.expected])} />
      </>
    );
  }
  if (id === "24") {
    if (!r.people.length) return <p className="text-sm text-slate-600">No controlled medications.</p>;
    return (
      <>
        {r.people.map((p: any) => (
          <div key={p.personKey} className="mb-5 break-inside-avoid">
            <h3 className="mb-1 text-base font-semibold"><Name p={p} link={link} /> <span className="text-sm font-normal text-slate-600">· {p.city === "(not assigned)" ? "Not assigned" : p.city}</span></h3>
            {p.medications.map((m: any) => (
              <div key={m.medKey} className="mb-2">
                <p className="text-sm font-medium">{m.medication}{m.discrepancy && <b className="ml-2 text-red-700">discrepancy {m.discrepancy}</b>}</p>
                <Table head={["Event", "When", "Count", "By", "Second person", "Detail"]} rows={m.events.map((e: any) => [e.kind, e.atUtc ? eastern(e.atUtc) : "—", e.countBefore || e.countAfter ? `${e.countBefore} → ${e.countAfter}` : e.count || "—", e.by || "—", e.verifier || "—", <span key="d">{e.detail || "—"}{e.docKey && link && <> · <a className="text-program underline" href={`/api/med-documents/${encodeURIComponent(e.docKey)}`} target="_blank" rel="noreferrer">signed receipt</a></>}</span>])} />
              </div>
            ))}
          </div>
        ))}
      </>
    );
  }
  if (id === "25") {
    const t = r.total;
    const counts = (c: any) => [c.visits, Object.entries(c.byKind).map(([k, n]) => `${k} ${n}`).join(", ") || "—", c.otcGiven, c.dosesGiven, c.dosesHeld, c.dosesRefused, c.dosesMissed, c.referrals, c.transports, c.dismissals];
    return (
      <>
        <p className="mb-3 text-sm">Session {r.session.start || "?"} to {r.session.end || "?"}{r.session.through && r.session.through !== r.session.end ? ` (so far: through ${r.session.through})` : ""} · scheduled doses given: <b>{r.givenRate === null ? "—" : `${r.givenRate}%`}</b> · staff visits: <b>{r.staffVisits}</b></p>
        <Table head={["", "Visits", "By kind", "OTC given", "Doses given", "Held", "Refused", "Missed", "Referrals", "Transports", "Dismissals"]} rows={[["Total", ...counts(t)], ...r.cities.map((c: any) => [c.city === "(not assigned)" ? "Not assigned" : c.city, ...counts(c)])]} />
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div><h3 className="mb-1 text-base font-semibold">Visits by disposition</h3><Table head={["Disposition", "Visits"]} rows={Object.entries(t.byDisposition).map(([k, n]) => [k, String(n)])} /></div>
          <div><h3 className="mb-1 text-base font-semibold">OTC by item</h3><Table head={["Item", "Given"]} rows={r.otcByItem.map((x: any) => [x.item, String(x.count)])} /></div>
          <div><h3 className="mb-1 text-base font-semibold">Notifications sent</h3><Table head={["Tier", "Sent"]} rows={r.notificationsSent.map((x: any) => [x.tier, String(x.count)])} /></div>
        </div>
        <p className="mt-2 text-xs text-slate-500">Counts only: no one is named on this report.</p>
      </>
    );
  }
  if (id === "15") {
    if (!r.lines.length) return <p className="text-sm text-slate-600">Nothing recorded on {r.date}.</p>;
    return <Table head={["Time", "Type", "Name", "City", "What", "Detail", "By"]} rows={r.lines.map((l: any) => [eastern(l.atUtc), l.type, <Name key="n" p={l} link={link} />, l.city || "Not assigned", l.what, l.detail || "—", l.by])} />;
  }
  if (id === "16") {
    if (!r.lines.length) return <p className="text-sm text-slate-600">Nothing open.</p>;
    return <Table head={["", "Name", "City", "What", "Due", "Detail"]} rows={r.lines.map((l: any) => [l.section, link && l.link ? <a key="n" className="text-program underline" href={l.link}>{l.lastName}, {l.firstName}</a> : `${l.lastName}, ${l.firstName}`, l.city || "Not assigned", l.what, l.dueOn || "—", l.detail])} />;
  }
  if (id === "13") {
    const c = r.counts;
    const who = (l: any) => <Name key="n" p={l} link={link} />;
    const section = (title: string, head: string[], rows: React.ReactNode[][]) => (
      <div className="mb-4 break-inside-avoid">
        <h3 className="mb-1 text-base font-semibold">{title}</h3>
        {rows.length ? <Table head={head} rows={rows} /> : <p className="text-sm text-slate-600">None.</p>}
      </div>
    );
    return (
      <>
        <p className="mb-3 text-sm">The last 24 hours, to {eastern(r.generatedAtUtc)}: <b>{c.visits}</b> {plural(c.visits, "visit")} ({c.injuries} {c.injuries === 1 ? "injury" : "injuries"}, {c.illnesses} {c.illnesses === 1 ? "illness" : "illnesses"}, {c.other} other) · <b>{c.referrals}</b> referred out · <b>{c.transports}</b> {plural(c.transports, "transport")} · <b>{c.openFollowUps}</b> {plural(c.openFollowUps, "follow-up")} open · <b>{c.restrictions}</b> on restriction · <b>{c.missed}</b> missed {plural(c.missed, "dose")} · <b>{c.dismissals}</b> {plural(c.dismissals, "dismissal")}.</p>
        {r.trends.length > 0 && <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm"><p className="font-semibold">Trends</p><ul className="list-disc pl-5">{r.trends.map((t: any, i: number) => <li key={i}>{t.text}</li>)}</ul></div>}
        {section("Visits", ["Time", "Name", "City", "Kind", "Complaint", "Disposition"], r.visits.map((l: any) => [eastern(l.atUtc), who(l), l.city || "Not assigned", l.kind, l.complaint, l.disposition || "—"]))}
        {section("Referred out", ["Name", "City", "To", "Complaint"], r.referrals.map((l: any) => [who(l), l.city || "Not assigned", l.to, l.complaint]))}
        {section("Transports", ["Name", "To", "With", "Back"], r.transports.map((l: any) => [who(l), l.to || "—", l.accompaniedBy || "—", l.returnedAtUtc ? eastern(l.returnedAtUtc) : "—"]))}
        {section("Follow-ups open", ["Name", "City", "Due", "Complaint"], r.openFollowUps.map((l: any) => [who(l), l.city || "Not assigned", l.dueOn || "—", l.complaint]))}
        {section("Activity restrictions", ["Name", "City", "Restriction", "Until"], r.restrictions.map((l: any) => [who(l), l.city || "Not assigned", l.text, l.untilOn || "—"]))}
        {section("Missed doses", ["Name", "City", "Medication", "Window", "Day"], r.missed.map((l: any) => [who(l), l.city || "Not assigned", l.medication, l.windowCode, l.scheduledOn]))}
        {section("Dismissals", ["Name", "City", "Left"], r.dismissals.map((l: any) => [who(l), l.city || "Not assigned", eastern(l.atUtc)]))}
        <p className="text-xs text-slate-500">Minimum necessary: one line per visit (the complaint and the disposition); never an assessment, a treatment, vitals or a note.</p>
      </>
    );
  }
  if (id === "14") {
    if (!r.cities.length) return <p className="text-sm text-slate-600">No one to watch.</p>;
    return (
      <>
        {r.cities.map((c: any) => (
          <div key={c.city} className="mb-5 break-inside-avoid">
            <h3 className="mb-1 text-base font-semibold">{c.city}</h3>
            <ul className="divide-y divide-slate-100 text-sm">
              {c.people.map((l: any) => (
                <li key={l.personKey} className="break-inside-avoid py-2">
                  <span className="font-medium"><Name p={l} link={link} /></span>
                  {l.checkOnToday && <span className="ml-2 rounded bg-amber-200 px-1.5 text-xs font-bold text-amber-950">Check on today: yes</span>}
                  {l.restriction && <span className="block">Restriction / hold: {l.restriction}{l.restrictionUntilOn ? ` (until ${l.restrictionUntilOn})` : ""}</span>}
                  {l.counselorNote && <span className="block">Note from the RN: {l.counselorNote}</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
        <p className="text-xs text-slate-500">For counselors: nothing else from the medical record is on this list.</p>
      </>
    );
  }
  if (id === "18") {
    if (!r.lines.length) return <p className="text-sm text-slate-600">Nothing is waiting for a provider.</p>;
    return <Table head={["Name", "City", "Medication", "Waiting", "Path", "Last step", "Chased by"]} rows={r.lines.map((l: any) => [<Name key="n" p={l} link={link} />, l.city || "Not assigned", l.medication, <span key="h" className={l.overAlert ? "font-semibold text-amber-900" : ""}>{l.hours} h{l.overAlert ? ` (over ${r.alertHours} h)` : ""}</span>, l.path, l.lastStep ? `${l.lastStep}, ${eastern(l.lastStepUtc)}` : "—", l.chasedBy || "—"])} />;
  }
  if (id === "19") {
    if (!r.lines.length) return <p className="text-sm text-slate-600">No staff visits.</p>;
    return <Table head={["Time", "Type", "Name", "Role", "What", "Detail", "By"]} rows={r.lines.map((l: any) => [eastern(l.atUtc), l.type, link ? <a key="n" className="font-medium text-program underline" href={`/staff/${encodeURIComponent(l.personKey)}`}>{l.lastName}, {l.firstName}</a> : `${l.lastName}, ${l.firstName}`, l.role || "—", l.what, l.detail || "—", l.by])} />;
  }
  if (id === "17") {
    if (!r.lines.length) return <p className="text-sm text-slate-600">No parent notifications.</p>;
    return <Table head={["Name", "City", "Tier", "Status", "To", "Drafted", "Due / sent / reached", "Template"]} rows={r.lines.map((l: any) => [<Name key="n" p={l} link={link} />, l.city || "Not assigned", `${l.method}${l.adultSelf ? " (to the attendee, 18+)" : ""}`, `${l.status}${l.overdue ? " · OVERDUE" : ""}`, `${l.toName}${l.toContact ? ` · ${l.toContact}` : ""}`, eastern(l.createdUtc), l.sentAtUtc ? `sent ${eastern(l.sentAtUtc)}` : l.reachedAtUtc ? `reached ${eastern(l.reachedAtUtc)}` : l.method === "Email" ? `due ${eastern(l.dueByUtc)}` : `${l.attempts.length} call(s)`, `${l.templateLabel}${l.templateBadge ? ` (${l.templateBadge})` : ""}`])} />;
  }
  if (id === "20") {
    if (!r.lines.length) return <p className="text-sm text-slate-600">Nothing is below par.</p>;
    return <Table head={["Item", "Category", "Par", "On hand", "Order"]} rows={r.lines.map((l: any) => [l.name, l.category, l.par, l.onHand, <span key="o" className="font-semibold">{l.order} {l.unit}</span>])} />;
  }
  if (id === "9") {
    return (
      <>
        <Table
          head={["Name", "Reference", "Medication", "Count", "Received by", "Verified by", "When", "Count change"]}
          rows={r.lines.map((l: any) => [<Name key="n" p={l} link={link} />, l.personKey, l.medication, l.count, l.receivedByEmail, l.verifiedByEmail || "—", eastern(l.atUtc), l.supersedesMedKey ? `Replaces an earlier receipt${l.earlierCount ? ` of ${l.earlierCount}` : ""}` : "—"])}
        />
        <h3 className="mb-2 mt-5 text-base font-semibold">Totals per medication (current receipts)</h3>
        <Table head={["Medication", "Count", "Receipts"]} rows={r.totals.map((t: any) => [t.medication, t.count, t.receipts])} />
      </>
    );
  }
  if (id === "1") {
    return (
      <>
        <div className="mb-4 grid gap-2 sm:grid-cols-4">
          {Object.entries(r.counts.status as Record<string, number>).map(([k, n]) => (
            <div key={k} className="rounded-md border border-slate-200 p-2 text-sm">
              <p className="text-xs text-slate-500">{REVIEW_LABEL[k] ?? k}</p>
              <p className="text-lg font-semibold">{n}</p>
            </div>
          ))}
        </div>
        <p className="mb-1 text-sm">
          <span className="font-medium">Family updated after review:</span> {r.counts.familyUpdatedAfterReview} · <span className="font-medium">On the roster:</span> {r.counts.total}
        </p>
        <p className="mb-3 text-sm">
          <span className="font-medium">Flagged:</span>{" "}
          {Object.entries(r.counts.flags as Record<string, number>).map(([k, n]) => `${FLAG_LABEL[k] ?? k} ${n}`).join(" · ")}
        </p>
        <Table
          head={["Name", "Reference", "City", "Review", "Flags", "Family updated"]}
          rows={r.people.map((p: any) => [
            <Name key="n" p={p} link={link} />,
            p.personKey,
            p.city || "—",
            <span key="s">
              {REVIEW_LABEL[p.reviewStatus] ?? p.reviewStatus}
              {p.reviewedAtUtc && <span className="block text-xs text-slate-500">{day(p.reviewedAtUtc)}</span>}
            </span>,
            p.flagTypes.map((t: string) => FLAG_LABEL[t] ?? t).join(", ") || "—",
            p.familyUpdatedAfterReview ? "Yes" : "—",
          ])}
        />
      </>
    );
  }
  if (id === "2") {
    return (
      <>
        <h3 className="mb-2 text-base font-semibold">Attendance holds</h3>
        <Table
          head={["Name", "Reference", "Status", "Since", "By", "Conditions"]}
          rows={r.holds.map((h: any) => [<Name key="n" p={h} link={link} />, h.personKey, HOLD_STATE[h.state] ?? h.state, day(h.atUtc), h.byEmail, h.conditions || "—"])}
        />
        <h3 className="mb-2 mt-5 text-base font-semibold">Document requests outstanding</h3>
        <p className="mb-2 text-xs text-slate-500">Overdue after {r.olderThanDays ?? "(not set)"} days (Config: Document request digest).</p>
        <Table
          head={["Name", "Reference", "Asked for", "Sent", "Days", "Reminders", "Last reminder"]}
          rows={r.requests.map((q: any) => [
            <Name key="n" p={q} link={link} />,
            q.personKey,
            `${q.template === "clarify" ? "Check" : "Upload"}: ${q.itemLabel}`,
            <span key="d">{day(q.sentUtc)}<span className="block text-xs text-slate-500">{q.sentByName}</span></span>,
            <span key="days" className={q.overdue ? "font-semibold text-red-800" : ""}>{q.days}{q.overdue ? " (overdue)" : ""}</span>,
            q.reminders,
            day(q.lastReminderUtc) || "—",
          ])}
        />
      </>
    );
  }
  if (id === "5") {
    const changed = r.people.filter((x: any) => x.changes.length || x.applicationMissing);
    return (
      <>
        <p className="mb-3 text-sm">
          <span className="font-medium">Frozen:</span> {r.frozen} · <span className="font-medium">Not frozen:</span> {r.notFrozen} · <span className="font-medium">Changed since the freeze:</span> {changed.length}
        </p>
        {changed.length === 0 ? (
          <p className="text-sm text-slate-600">{r.frozen ? "No one's application has changed since the freeze." : "Nothing frozen yet."}</p>
        ) : (
          <ul className="space-y-3">
            {changed.map((x: any) => (
              <li key={x.personKey} className="break-inside-avoid rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
                <p><Name p={x} link={link} /> · {x.personKey} <span className="text-xs text-slate-500">frozen {day(x.frozenAtUtc)}</span></p>
                {x.applicationMissing && <p className="text-amber-900">The application can no longer be found.</p>}
                {x.changes.map((c: any) => (
                  <div key={c.section} className="mt-1">
                    <p className="font-medium">{c.label}</p>
                    <ul className="ml-4 list-disc">
                      {c.removed.map((l: string) => <li key={"r" + l} className="break-words"><span className="font-medium text-red-800">In the seed, not now:</span> {l}</li>)}
                      {c.added.map((l: string) => <li key={"a" + l} className="break-words"><span className="font-medium text-emerald-800">Now, not in the seed:</span> {l}</li>)}
                    </ul>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }
  if (id === "3") {
    return (
      <Table
        head={["Name", "Reference", "City", "Reasons"]}
        rows={r.people.map((p: any) => [<Name key="n" p={p} link={link} />, p.personKey, p.city || "—", p.reasons.map((x: string) => REASON_LABEL[x] ?? x).join(", ")])}
      />
    );
  }
  if (!r.session) return <p className="text-sm text-amber-900">The session dates are not set (Config: Session dates), so ages can't be worked out.</p>;
  return (
    <>
      <p className="mb-3 text-sm text-slate-700">Check-in {r.session.checkInOn}; last day {r.session.end}.</p>
      <h3 className="mb-2 text-base font-semibold">18 at check-in</h3>
      <Table head={["Name", "Reference", "Date of birth", "City"]} rows={r.adultAtCheckIn.map((p: any) => [<Name key="n" p={p} link={link} />, p.personKey, p.dateOfBirth, p.city || "—"])} />
      <h3 className="mb-2 mt-5 text-base font-semibold">Turning 18 during the session, day by day</h3>
      <ul className="space-y-2">
        {r.days.map((d: any) => (
          <li key={d.date} className="text-sm break-inside-avoid">
            <p className="font-medium">{longDate(d.date)}</p>
            {d.people.length === 0 ? (
              <p className="text-slate-500">No one.</p>
            ) : (
              <ul className="ml-4 list-disc">
                {d.people.map((p: any) => (
                  <li key={p.personKey}>
                    <Name p={p} link={link} /> · {p.personKey} · born {p.dateOfBirth}{p.city ? ` · ${p.city}` : ""}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
      {r.unknownAge.length > 0 && (
        <p className="mt-3 text-sm text-amber-900">Date of birth unreadable for: {r.unknownAge.map((p: any) => `${p.lastName}, ${p.firstName} (${p.personKey})`).join("; ")}</p>
      )}
    </>
  );
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/** The window, city and day of a #10–#12 report: kept in the address, so print and CSV carry them. */
function PassParams({ q, onChange }: { q: URLSearchParams; onChange: (q: URLSearchParams) => void }) {
  const [o, setO] = useState<{ windows: { code: string; name: string; startTime: string; endTime: string }[]; cities: string[]; today: string } | null>(null);
  useEffect(() => {
    void api<NonNullable<typeof o>>("GET", "/api/print/options").then((r) => r.ok && setO(r.data));
  }, []);
  const set = (k: string, v: string) => {
    const n = new URLSearchParams(q);
    if (v) n.set(k, v);
    else n.delete(k);
    onChange(n);
  };
  if (!o) return null;
  return (
    <div className="section-card flex flex-wrap items-end gap-3 print:hidden">
      <div><label htmlFor="rp-w" className="field-label">Window</label><select id="rp-w" className="field-input" value={q.get("window") ?? ""} onChange={(e) => set("window", e.target.value)}><option value="">The window now</option>{o.windows.map((w) => <option key={w.code} value={w.code}>{w.name} ({w.startTime}–{w.endTime})</option>)}</select></div>
      <div><label htmlFor="rp-c" className="field-label">City</label><select id="rp-c" className="field-input" value={q.get("city") ?? ""} onChange={(e) => set("city", e.target.value)}><option value="">Every city</option>{o.cities.map((c) => <option key={c} value={c}>{c}</option>)}<option value="(not assigned)">Not assigned</option></select></div>
      <div><label htmlFor="rp-d" className="field-label">Day</label><input id="rp-d" type="date" className="field-input" value={q.get("date") ?? o.today} onChange={(e) => set("date", e.target.value)} /></div>
    </div>
  );
}

function ReportScreen({ id, me }: { id: string; me: Me }) {
  const [data, setData] = useState<{ title: string; report: unknown; canExport: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search));
  const qs = (PASS_REPORTS.includes(id) || DAY_REPORTS.includes(id)) && q.toString() ? `?${q}` : "";
  const load = () => void api<{ title: string; report: unknown; canExport: boolean }>("GET", `/api/reports/${id}${qs}`).then((r) => (r.ok ? (setData(r.data), setError(null)) : setError(r.data.error ?? "Could not load the report.")));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [id, qs]);
  const changeQ = (n: URLSearchParams) => {
    setQ(n);
    window.history.replaceState(null, "", `/reports/${id}${n.toString() ? `?${n}` : ""}`);
  };
  return (
    <section>
      <p className="mb-3 text-sm"><a href="/reports" className="text-program underline">← Reports</a></p>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">#{id} {TITLES[id]}</h2>
        {data?.canExport && (
          <div className="flex flex-wrap gap-2">
            {id === "7" && <a className="btn-secondary" href="/status-board">Wall screen</a>}
            <a className="btn-secondary" href={`/reports/${id}/print${qs}`}>Print view</a>
            <a className="btn-secondary" href={`/api/reports/${id}/csv${qs}`} download>Download CSV</a>
          </div>
        )}
      </div>
      {error && <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>}
      {!data && !error && <p className="text-sm text-slate-600">Loading…</p>}
      {id === "5" && <FreezeCard onFrozen={load} />}
      {id === "13" && me.capabilities.includes("jobs.run") && <div className="section-card print:hidden"><p className="mb-2 text-sm text-slate-700">Emailed to the Director at the times in Config (Director's report times), when Scheduled emails says so.</p><RunNow job="directorReport" /></div>}
      {PASS_REPORTS.includes(id) && <PassParams q={q} onChange={changeQ} />}
      {DAY_REPORTS.includes(id) && (
        <div className="section-card print:hidden">
          <label htmlFor="rp-day" className="field-label">Day</label>
          <input id="rp-day" type="date" className="field-input max-w-[12rem]" value={q.get("date") ?? ""} onChange={(e) => { const n = new URLSearchParams(q); if (e.target.value) n.set("date", e.target.value); else n.delete("date"); changeQ(n); }} />
        </div>
      )}
      {data && id === "17" && (
        <div className="section-card">
          <NotificationQueue lines={(data.report as { lines: never[] }).lines} me={me} onChanged={load} />
        </div>
      )}
      {data && id !== "17" && (
        <div className="section-card">
          <ReportBody id={id} r={data.report} link />
        </div>
      )}
    </section>
  );
}

/** Freeze the medication seed (plan §9.1 #1): System Admin, on or after amendments close. */
function FreezeCard({ onFrozen }: { onFrozen: () => void }) {
  const [st, setSt] = useState<{ status: { closeDate: string; closeDateSource: string; enabled: boolean; reason: string; frozen: number; notFrozen: number }; canFreeze: boolean } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => void api<NonNullable<typeof st>>("GET", "/api/seed/freeze").then((r) => r.ok && setSt(r.data));
  useEffect(load, []);
  if (!st) return null;
  const s = st.status;
  return (
    <div className="section-card">
      <h3 className="mb-1 text-base font-semibold">Freeze the medication seed</h3>
      <p className="text-sm text-slate-700">
        Amendments close: <span className="font-medium">{s.closeDate || "not set"}</span>
        {s.closeDateSource === "testConfig" ? " (test app setting)" : ""} · frozen {s.frozen} · not frozen {s.notFrozen}
      </p>
      {!s.enabled && <p className="mt-1 text-sm text-amber-900">{s.reason}</p>}
      {st.canFreeze && s.enabled && s.notFrozen > 0 && (
        <button
          type="button"
          className="btn-primary mt-2"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            const r = await api<{ result: { frozen: string[]; noApplication: string[] } }>("POST", "/api/seed/freeze");
            setBusy(false);
            setMsg(r.ok ? `Frozen for ${r.data.result.frozen.length} student${r.data.result.frozen.length === 1 ? "" : "s"}${r.data.result.noApplication.length ? `; ${r.data.result.noApplication.length} without a readable application` : ""}.` : r.data.error ?? "Not frozen.");
            load();
            onFrozen();
          }}
        >
          Freeze {s.notFrozen} student{s.notFrozen === 1 ? "" : "s"}
        </button>
      )}
      {msg && <p className="mt-2 text-sm" role="status">{msg}</p>}
      <p className="field-hint">Each student's medications, allergies, OTC permissions, conditions, accommodations, self-carry and documents are saved once and never changed. The med station works from that saved seed.</p>
    </div>
  );
}

/** The print view (plan §7.2): the same report, no links, the app's chrome hidden when printed. */
function PrintView({ id, me }: { id: string; me: Me }) {
  const [data, setData] = useState<{ title: string; report: unknown; generatedAtUtc: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void api<{ title: string; report: unknown; generatedAtUtc: string }>("GET", `/api/reports/${id}/print${PASS_REPORTS.includes(id) || DAY_REPORTS.includes(id) ? window.location.search : ""}`).then((r) => (r.ok ? setData(r.data) : setError(r.data.error ?? "Could not load the report.")));
  }, [id]);
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 print:hidden">
        <a href={`/reports/${id}`} className="text-sm text-program underline">← Back to the report</a>
        {data && <button type="button" className="btn-primary" onClick={() => window.print()}>Print</button>}
      </div>
      {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
      {!data && !error && <p className="text-sm text-slate-600">Loading…</p>}
      {data && (
        <div className="bg-white p-2 sm:p-4 print:p-0">
          <h2 className="text-lg font-semibold">#{id} {data.title}</h2>
          <p className="mb-1 text-xs text-slate-600">Printed from Medical Administration {eastern(data.generatedAtUtc)} by {me.name}.</p>
          <p className="mb-3 text-xs font-semibold text-slate-800">Confidential health information: keep it with you, and shred it when it is no longer needed.</p>
          <ReportBody id={id} r={data.report} link={false} />
        </div>
      )}
    </section>
  );
}

// ---- the Director digest and "Run now" -----------------------------------------------------------------

interface DigestResponse {
  data: {
    forDate: string;
    olderThanDays: number | null;
    requests: { personKey: string; name: string; itemLabel: string; template: string; sentUtc: string; days: number; reminders: number; lastReminderUtc: string }[];
    holds: { personKey: string; name: string; state: string; sinceUtc: string; byEmail: string }[];
    pendingAuthorizations: { personKey: string; name: string; medications: number; sinceUtc: string; hours: number }[];
    alertHours: number;
    askConsentToday: { personKey: string; name: string }[];
    recipients: string[];
    problems: string[];
  };
  history: { forDate: string; trigger: string; sentUtc: string; requests: number; holds: number; route: string }[];
  scheduled: { docRequestReminders: boolean; directorDigest: boolean };
  canRun: boolean;
}

const JOB_LABEL = { docRequestReminders: "Document-request reminders", directorDigest: "Director digest", directorReport: "Director's report (#13)" } as const;

function describeRun(job: keyof typeof JOB_LABEL, r: Record<string, unknown>): string {
  if (job === "docRequestReminders") {
    if (r.ok === false) return `Not run: ${String(r.reason ?? "not configured")}`;
    const sent = (r.sent as unknown[]).length;
    const skipped = (r.skipped as unknown[]).length;
    return `${sent} reminder${sent === 1 ? "" : "s"} sent${skipped ? `, ${skipped} skipped` : ""} (${String(r.open)} open request${r.open === 1 ? "" : "s"}).`;
  }
  if (job === "directorReport" && r.outcome === "sent") return r.route === "outbox" ? "Director's report written to the Test Outbox." : "Director's report sent.";
  if (r.outcome === "sent") return r.route === "outbox" ? "Digest written to the Test Outbox." : "Digest sent.";
  if (r.outcome === "nothing") return "Nothing to report, so nothing was sent.";
  return `Not sent: ${String(r.reason ?? r.outcome)}`;
}

function RunNow({ job, onRan }: { job: keyof typeof JOB_LABEL; onRan?: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        className="btn-secondary"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setMsg(null);
          const r = await api<{ result: Record<string, unknown> }>("POST", `/api/jobs/${job}/run`);
          setBusy(false);
          setMsg(r.ok ? describeRun(job, r.data.result) : r.data.error ?? "Didn't run.");
          onRan?.();
        }}
      >
        {busy ? "Running…" : `Run ${JOB_LABEL[job].toLowerCase()} now`}
      </button>
      {msg && <span className="text-sm text-slate-700" role="status">{msg}</span>}
    </div>
  );
}

function ScheduledEmails({ d, onRan }: { d: DigestResponse; onRan: () => void }) {
  return (
    <div className="section-card">
      <h3 className="mb-1 text-base font-semibold">Scheduled emails</h3>
      <p className="mb-3 text-sm text-slate-700">
        Reminders are checked every 15 minutes: <span className="font-medium">{d.scheduled.docRequestReminders ? "on" : "off"}</span>. The digest goes daily from 07:00 Eastern:{" "}
        <span className="font-medium">{d.scheduled.directorDigest ? "on" : "off"}</span>. The Director's report (#13) goes at its two times (Config: Director's report times):{" "}
        <span className="font-medium">{(d.scheduled as { directorReport?: boolean }).directorReport ? "on" : "off"}</span>. (Config: Scheduled emails.)
      </p>
      {d.canRun && (
        <div className="space-y-2">
          <RunNow job="docRequestReminders" onRan={onRan} />
          <RunNow job="directorDigest" onRan={onRan} />
          <RunNow job="directorReport" onRan={onRan} />
        </div>
      )}
    </div>
  );
}

function useDigest(): [DigestResponse | null, string | null, () => void] {
  const [d, setD] = useState<DigestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => void api<DigestResponse>("GET", "/api/digest").then((r) => (r.ok ? setD(r.data) : setError(r.data.error ?? "Could not load the digest.")));
  useEffect(load, []);
  return [d, error, load];
}

function DigestScreen() {
  const [d, error, reload] = useDigest();
  return (
    <section>
      <p className="mb-3 text-sm"><a href="/reports" className="text-program underline">← Reports</a></p>
      <h2 className="mb-1 text-xl font-semibold">Director digest</h2>
      {error && <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>}
      {!d && !error && <p className="text-sm text-slate-600">Loading…</p>}
      {d && (
        <>
          <p className="mb-4 text-sm text-slate-600">
            What today's email lists ({d.data.forDate}). It goes to {d.data.recipients.length ? d.data.recipients.join(", ") : "no one yet"}.
          </p>
          {d.data.problems.length > 0 && (
            <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="note">
              {d.data.problems.map((p) => <p key={p}>{p}</p>)}
            </div>
          )}
          <div className="section-card">
            <h3 className="mb-2 text-base font-semibold">Attendance holds</h3>
            <Table
              head={["Name", "Reference", "Status", "Since"]}
              rows={d.data.holds.map((h) => [
                <a key="n" className="font-medium text-program underline" href={`/review/${encodeURIComponent(h.personKey)}`}>{h.name}</a>,
                h.personKey,
                HOLD_STATE[h.state] ?? h.state,
                day(h.sinceUtc),
              ])}
            />
          </div>
          <div className="section-card">
            <h3 className="mb-2 text-base font-semibold">Ask consent today (turning 18)</h3>
            <Table
              head={["Name", "Reference"]}
              rows={d.data.askConsentToday.map((x) => [<a key="n" className="font-medium text-program underline" href={`/station/${encodeURIComponent(x.personKey)}`}>{x.name}</a>, x.personKey])}
            />
          </div>
          <div className="section-card">
            <h3 className="mb-2 text-base font-semibold">Pending authorizations over {d.data.alertHours} hours</h3>
            <Table
              head={["Name", "Reference", "Medications", "Waiting since", "Hours"]}
              rows={d.data.pendingAuthorizations.map((x) => [
                <a key="n" className="font-medium text-program underline" href={`/station/${encodeURIComponent(x.personKey)}`}>{x.name}</a>,
                x.personKey,
                x.medications,
                day(x.sinceUtc),
                x.hours,
              ])}
            />
          </div>
          <div className="section-card">
            <h3 className="mb-2 text-base font-semibold">Document requests open {d.data.olderThanDays ?? "N"} days or more</h3>
            <Table
              head={["Name", "Reference", "Asked for", "Sent", "Days", "Reminders"]}
              rows={d.data.requests.map((q) => [
                <a key="n" className="font-medium text-program underline" href={`/review/${encodeURIComponent(q.personKey)}`}>{q.name}</a>,
                q.personKey,
                q.itemLabel,
                day(q.sentUtc),
                q.days,
                q.reminders,
              ])}
            />
          </div>
          <ScheduledEmails d={d} onRan={reload} />
          <div className="section-card">
            <h3 className="mb-2 text-base font-semibold">Recently sent</h3>
            <Table
              head={["For", "Sent", "How", "Requests", "Holds"]}
              rows={d.history.map((x) => [x.forDate, eastern(x.sentUtc), `${x.trigger === "manual" ? "Run now" : "Scheduled"}${x.route === "outbox" ? " (Test Outbox)" : ""}`, x.requests, x.holds])}
            />
          </div>
        </>
      )}
    </section>
  );
}

function ReportsIndex({ me }: { me: Me }) {
  const [d, , reload] = useDigest();
  return (
    <section>
      <h2 className="mb-4 text-xl font-semibold">Reports</h2>
      <div className="section-card">
        <ul className="divide-y divide-slate-100">
          {Object.keys(TITLES).map((id) => (
            <li key={id} className="py-2">
              <a className="font-medium text-program underline" href={`/reports/${id}`}>#{id} {TITLES[id]}</a>
              <p className="text-sm text-slate-600">{BLURB[id]}</p>
            </li>
          ))}
          <li className="py-2">
            <a className="font-medium text-program underline" href="/eow">#21 Parent end-of-week reports</a>
            <p className="text-sm text-slate-600">One per student who attended: medications given, OTC items, visits. A System Admin drafts them into #17; an RN or Supervisor releases each. Print one or all.</p>
          </li>
          <li className="py-2">
            <a className="font-medium text-program underline" href="/reports/concerns">#26 Concerns for next year</a>
            <p className="text-sm text-slate-600">Anyone adds what should change next year (supplies, process, facility); a System Admin closes each with a note. Open and closed counts; CSV.</p>
          </li>
          {me.capabilities.includes("audit.export") && (
            <li className="py-2">
              <a className="font-medium text-program underline" href="/reports/audit">#27 Audit log export</a>
              <p className="text-sm text-slate-600">System Admin: every sign-in, lock, read and write for a date range, as CSV (who, what kind, which person; never the record's contents).</p>
            </li>
          )}
          <li className="py-2">
            <a className="font-medium text-program underline" href="/reports/digest">Director digest</a>
            <p className="text-sm text-slate-600">Document requests open N days or more, and attendance holds: the daily email, as a screen.</p>
          </li>
        </ul>
      </div>
      {d && <ScheduledEmails d={d} onRan={reload} />}
    </section>
  );
}

/** Report #7 for a wall screen (plan §9.1 #12): big type, refreshed every 60 seconds; print from the report's own print view. */
export function StatusBoard(): React.ReactElement {
  const [r, setR] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const load = () => void api<{ report: unknown }>("GET", "/api/reports/7").then((x) => (x.ok ? (setR(x.data.report), setError(null)) : setError(x.data.error ?? "Could not load.")));
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 print:hidden">
        <a href="/reports/7" className="text-sm text-program underline">← #7 Med-station status board</a>
        <span className="text-sm text-slate-600">{r ? `Updated ${eastern(r.generatedAtUtc)}. Refreshes every minute.` : ""}</span>
      </div>
      <h2 className="mb-3 text-3xl font-bold">Med station</h2>
      {error && <p className="text-xl text-red-700" role="alert">{error}</p>}
      {r ? <Board r={r} big now={Date.parse(r.generatedAtUtc)} /> : !error && <p className="text-xl text-slate-600">Loading…</p>}
    </section>
  );
}

export function Reports({ path, me }: { path: string; me: Me }): React.ReactElement {
  if (path === "/status-board") return <StatusBoard />;
  const m = /^\/reports(?:\/([^/]+))?(?:\/(print))?$/.exec(path);
  const id = m?.[1];
  if (id === "digest") return <DigestScreen />;
  if (id === "concerns") return <Concerns />;
  if (id === "audit") return <AuditExport />;
  if (id && TITLES[id]) return m?.[2] ? <PrintView id={id} me={me} /> : <ReportScreen id={id} me={me} />;
  return <ReportsIndex me={me} />;
}

/** #27 (System Admin): the audit log for a date range, as CSV. The export is itself audited on the server. */
function AuditExport() {
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date(appNow()));
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  return (
    <section>
      <p className="mb-3 text-sm"><a href="/reports" className="text-program underline">← Reports</a></p>
      <h2 className="mb-1 text-xl font-semibold">#27 Audit log export</h2>
      <p className="mb-4 text-sm text-slate-600">Every sign-in, lock event, read and write in the date range (Eastern days): when, the kind of event, who, and which person or record key. Never the record's contents.</p>
      <div className="section-card flex flex-wrap items-end gap-3">
        <div><label htmlFor="ae-from" className="field-label">From</label><input id="ae-from" type="date" className="field-input" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><label htmlFor="ae-to" className="field-label">To</label><input id="ae-to" type="date" className="field-input" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <a className="btn-primary inline-flex min-h-11 items-center" href={`/api/audit/export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`}>Download CSV</a>
      </div>
    </section>
  );
}
